// Enhanced theme utility functions and constants

export interface ThemeColors {
  background: {
    primary: string;
    secondary: string;
    elevated: string;
    overlay: string;
  };
  text: {
    primary: string;
    secondary: string;
    muted: string;
    inverse: string;
  };
  border: {
    default: string;
    focus: string;
    hover: string;
  };
  accent: {
    primary: string;
    secondary: string;
    success: string;
    error: string;
    warning: string;
  };
}

// Professional color palette with proper black/gray dark mode
export const lightThemeColors: ThemeColors = {
  background: {
    primary: '#ffffff',
    secondary: '#f9fafb',
    elevated: '#ffffff',
    overlay: 'rgba(0, 0, 0, 0.5)',
  },
  text: {
    primary: '#111827',
    secondary: '#6b7280',
    muted: '#9ca3af',
    inverse: '#ffffff',
  },
  border: {
    default: '#e5e7eb',
    focus: '#4f46e5',
    hover: '#d1d5db',
  },
  accent: {
    primary: '#27909c',
    secondary: '#4f46e5',
    success: '#10b981',
    error: '#ef4444',
    warning: '#f59e0b',
  },
};

export const darkThemeColors: ThemeColors = {
  background: {
    primary: '#000000',
    secondary: '#111827',
    elevated: '#1f2937',
    overlay: 'rgba(0, 0, 0, 0.8)',
  },
  text: {
    primary: '#ffffff',
    secondary: '#d1d5db',
    muted: '#9ca3af',
    inverse: '#000000',
  },
  border: {
    default: '#374151',
    focus: '#6366f1',
    hover: '#4b5563',
  },
  accent: {
    primary: '#2dd4bf',
    secondary: '#6366f1',
    success: '#34d399',
    error: '#f87171',
    warning: '#fbbf24',
  },
};

export interface ThemeUtils {
  getColors: (isDarkMode: boolean) => ThemeColors;
  getResponsiveClasses: (baseClasses: string) => string;
  getThemeClass: (isDarkMode: boolean, lightClass: string, darkClass: string) => string;
}

export const themeUtils: ThemeUtils = {
  getColors: (isDarkMode: boolean): ThemeColors => {
    return isDarkMode ? darkThemeColors : lightThemeColors;
  },
  
  getResponsiveClasses: (baseClasses: string): string => {
    return `${baseClasses} mobile-responsive`;
  },
  
  getThemeClass: (isDarkMode: boolean, lightClass: string, darkClass: string): string => {
    return isDarkMode ? darkClass : lightClass;
  },
};

// Enhanced theme-aware class combinations with proper black/gray dark mode
export const themeClasses = {
  // Background classes
  background: {
    primary: 'bg-white dark:bg-black',
    secondary: 'bg-gray-50 dark:bg-gray-900',
    elevated: 'bg-white dark:bg-gray-800',
    overlay: 'bg-black/50 dark:bg-black/80',
  },
  
  // Text classes
  text: {
    primary: 'text-gray-900 dark:text-white',
    secondary: 'text-gray-600 dark:text-gray-300',
    muted: 'text-gray-500 dark:text-gray-400',
    inverse: 'text-white dark:text-black',
  },
  
  // Border classes
  border: {
    default: 'border-gray-200 dark:border-gray-700',
    focus: 'border-indigo-500 dark:border-indigo-400',
    hover: 'border-gray-300 dark:border-gray-600',
  },
  
  // Interactive states
  hover: {
    background: 'hover:bg-gray-50 dark:hover:bg-gray-800',
    text: 'hover:text-gray-900 dark:hover:text-white',
  },
  
  // Focus states
  focus: {
    ring: 'focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 focus:ring-offset-2 dark:focus:ring-offset-gray-900',
    outline: 'focus:outline-none',
  },
  
  // Card styles
  card: {
    base: 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm',
    hover: 'hover:shadow-md hover:border-gray-300 dark:hover:border-gray-600 transition-all duration-200',
  },
  
  // Button styles
  button: {
    primary: 'bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white',
    secondary: 'bg-white hover:bg-gray-50 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600',
    ghost: 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300',
  },
} as const;

// Mobile-responsive utilities
export const responsiveClasses = {
  // Touch targets
  touchTarget: 'min-h-[44px] min-w-[44px]',
  
  // Spacing
  spacing: {
    mobile: 'p-4 sm:p-6',
    desktop: 'p-6 lg:p-8',
  },
  
  // Typography
  typography: {
    mobile: 'text-sm sm:text-base',
    heading: 'text-lg sm:text-xl lg:text-2xl',
  },
  
  // Grid layouts
  grid: {
    responsive: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6',
    stats: 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4',
  },
} as const;

// Utility function to combine theme and responsive classes
export const combineClasses = (...classes: (string | undefined | false)[]): string => {
  return classes.filter(Boolean).join(' ');
};

// Theme-aware animation classes
export const animationClasses = {
  fadeIn: 'animate-fade-in',
  slideIn: 'animate-slide-in-left',
  scaleIn: 'animate-scale-in',
  transition: 'transition-all duration-200 ease-in-out',
  microInteraction: 'transition-all duration-150 ease-in-out',
} as const;